import React, { useEffect, useState, useMemo } from "react";
import { AppLayout } from "../components/tabon/AppLayout";
import { api, formatError } from "../lib/api";
import { toast } from "sonner";
import { ChevronDown, ChevronRight, Search, X, CheckSquare } from "lucide-react";

export default function MenuAvailability() {
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [collapsed, setCollapsed] = useState({});
  const [confirmMarkAll, setConfirmMarkAll] = useState(false);
  const [markingAll, setMarkingAll] = useState(false);

  const load = async () => {
    try {
      const [iRes, cRes] = await Promise.all([
        api.get("/staff/menu/availability"),
        api.get("/menu/categories"),
      ]);
      setItems(iRes.data);
      setCategories(cRes.data);
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const toggleAvailability = async (item) => {
    if (item.pos_managed) return;   // read-only — availability comes from the POS
    const newVal = !item.is_available;
    // Optimistic update
    setItems(prev => prev.map(i => i.id === item.id ? { ...i, is_available: newVal } : i));
    try {
      await api.patch(`/staff/menu/item/${item.id}/availability`, { is_available: newVal });
    } catch (err) {
      // Revert on failure
      setItems(prev => prev.map(i => i.id === item.id ? { ...i, is_available: item.is_available } : i));
      toast.error(formatError(err));
    }
  };

  const markAllAvailable = async () => {
    setMarkingAll(true);
    // POS-managed items can't be toggled here (the POS owns their availability).
    const unavailable = items.filter(i => !i.is_available && !i.pos_managed);
    if (unavailable.length === 0) {
      toast.info("All items are already available");
      setMarkingAll(false);
      setConfirmMarkAll(false);
      return;
    }
    // Optimistic — leave POS-managed items untouched
    setItems(prev => prev.map(i => (i.pos_managed ? i : { ...i, is_available: true })));
    setConfirmMarkAll(false);
    try {
      const updates = unavailable.map(i => ({ item_id: i.id, is_available: true }));
      await api.patch("/staff/menu/bulk-availability", { updates });
      toast.success(`${unavailable.length} item(s) marked available`);
    } catch (err) {
      toast.error(formatError(err));
      await load(); // Reload on failure
    } finally {
      setMarkingAll(false);
    }
  };

  const toggleCollapse = (catId) =>
    setCollapsed(prev => ({ ...prev, [catId]: !prev[catId] }));

  // Build grouped data, filtered by search
  const grouped = useMemo(() => {
    const q = search.trim().toLowerCase();
    return categories.map(cat => {
      const catItems = items.filter(i => i.category_id === cat.id);
      const filtered = q
        ? catItems.filter(i => i.name.toLowerCase().includes(q))
        : catItems;
      return { ...cat, items: filtered };
    }).filter(cat => cat.items.length > 0 || !q);
  }, [categories, items, search]);

  const soldOutCount = items.filter(i => !i.is_available).length;
  // Only venue-toggleable items count toward "Mark All Available".
  const markableSoldOut = items.filter(i => !i.is_available && !i.pos_managed).length;

  // Name the POS so the read-only note is actionable ("Mark unavailable in Petpooja").
  // Provider-neutral: falls back to "your POS" when the provider isn't known.
  const providerOf = (item) => {
    const p = item?.pos_provider;
    return p ? p.charAt(0).toUpperCase() + p.slice(1) : "your POS";
  };
  const posProviderLabel = useMemo(() => {
    const p = items.find(i => i.pos_managed && i.pos_provider)?.pos_provider;
    return p ? p.charAt(0).toUpperCase() + p.slice(1) : "your POS";
  }, [items]);

  return (
    <AppLayout>
      <div className="max-w-2xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-black tracking-tight">Menu Availability</h1>
            <p className="text-sm text-[#A0A0A0] mt-0.5">
              Toggle items in real-time. Changes take effect immediately.
              {soldOutCount > 0 && (
                <span className="ml-2 text-red-400 font-semibold">{soldOutCount} sold out</span>
              )}
            </p>
          </div>
          <button
            onClick={() => setConfirmMarkAll(true)}
            disabled={markingAll || markableSoldOut === 0}
            data-testid="mark-all-available"
            className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-semibold rounded-lg px-4 py-2.5 min-h-[40px] transition"
          >
            <CheckSquare size={15} />
            Mark All Available
          </button>
        </div>

        {/* Search */}
        <div className="relative mb-4">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A0A0A0]" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search items…"
            data-testid="availability-search"
            className="w-full bg-[#1A1A1A] border border-white/10 rounded-lg pl-9 pr-9 py-2.5 text-sm text-white placeholder:text-[#6B6B6B] focus:border-[#9D00FF] focus:ring-1 focus:ring-[#9D00FF]/40"
          />
          {search && (
            <button onClick={() => setSearch("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#A0A0A0] hover:text-white">
              <X size={14} />
            </button>
          )}
        </div>

        {loading ? (
          <div className="text-center py-16 text-[#A0A0A0] text-sm">Loading menu…</div>
        ) : (
          <div className="flex flex-col gap-3">
            {grouped.map((cat) => {
              const isOpen = !collapsed[cat.id];
              return (
                <div key={cat.id} className="bg-[#1A1A1A] border border-white/10 rounded-xl overflow-hidden">
                  {/* Category header */}
                  <button
                    onClick={() => toggleCollapse(cat.id)}
                    className="w-full flex items-center justify-between px-4 py-3.5 hover:bg-white/5 transition"
                    data-testid={`cat-toggle-${cat.id}`}
                  >
                    <div className="flex items-center gap-2">
                      <span className="font-bold text-white">{cat.name}</span>
                      <span className="text-[10px] text-[#A0A0A0] bg-white/10 rounded-full px-2 py-0.5">
                        {cat.items.length} items
                      </span>
                      {cat.items.some(i => !i.is_available) && (
                        <span className="text-[10px] text-red-400 bg-red-500/10 border border-red-500/20 rounded-full px-2 py-0.5">
                          {cat.items.filter(i => !i.is_available).length} sold out
                        </span>
                      )}
                    </div>
                    {isOpen ? <ChevronDown size={16} className="text-[#A0A0A0]" /> : <ChevronRight size={16} className="text-[#A0A0A0]" />}
                  </button>

                  {/* Items */}
                  {isOpen && (
                    <div className="border-t border-white/5 divide-y divide-white/5">
                      {cat.items.map((item) => (
                        <div
                          key={item.id}
                          data-testid={`avail-item-${item.id}`}
                          className="flex items-center justify-between px-4 py-3 hover:bg-white/5 transition"
                        >
                          <div className="flex-1 min-w-0 pr-4">
                            <div className={`font-medium text-sm ${item.is_available ? "text-white" : "text-[#A0A0A0] line-through"}`}>
                              {item.name}
                            </div>
                            {item.pos_managed && (
                              <div className="text-[11px] text-[#A0A0A0] mt-0.5">Mark unavailable in {providerOf(item)} — it updates here within seconds</div>
                            )}
                          </div>
                          {/* Toggle — read-only when the item's availability is owned by the POS */}
                          {item.pos_managed ? (
                            <span
                              data-testid={`toggle-${item.id}`}
                              title={`Availability for this item is managed in ${providerOf(item)} — mark it there and it updates here within seconds.`}
                              className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-bold border min-w-[110px] justify-center cursor-not-allowed opacity-60 ${
                                item.is_available
                                  ? "bg-emerald-500/10 border-emerald-500/25 text-emerald-400/70"
                                  : "bg-red-500/10 border-red-500/25 text-red-400/70"
                              }`}
                            >
                              <span className={`w-2 h-2 rounded-full ${item.is_available ? "bg-emerald-400/60" : "bg-red-400/60"}`} />
                              {item.is_available ? "Available" : "Sold Out"}
                            </span>
                          ) : (
                            <button
                              onClick={() => toggleAvailability(item)}
                              data-testid={`toggle-${item.id}`}
                              className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-bold border transition min-w-[110px] justify-center ${
                                item.is_available
                                  ? "bg-emerald-500/15 border-emerald-500/40 text-emerald-400 hover:bg-emerald-500/25"
                                  : "bg-red-500/15 border-red-500/40 text-red-400 hover:bg-red-500/25"
                              }`}
                            >
                              <span className={`w-2 h-2 rounded-full ${item.is_available ? "bg-emerald-400" : "bg-red-400"}`} />
                              {item.is_available ? "Available" : "Sold Out"}
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
            {grouped.length === 0 && (
              <div className="text-center py-12 text-[#A0A0A0] text-sm">
                {search ? "No items match your search." : "No menu items found."}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Confirm modal */}
      {confirmMarkAll && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-[#1A1A1A] border border-white/10 rounded-xl p-6 w-full max-w-sm">
            <h3 className="text-lg font-bold mb-2">Mark All Available?</h3>
            <p className="text-sm text-[#A0A0A0] mb-5">
              This will restore all <span className="text-white font-semibold">{markableSoldOut} sold-out</span> items to available.
              {soldOutCount > markableSoldOut && (
                <span className="block mt-1 text-[12px]">
                  {soldOutCount - markableSoldOut} POS-managed item(s) are excluded — their availability is managed in {posProviderLabel}.
                </span>
              )}
            </p>
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => setConfirmMarkAll(false)}
                className="bg-[#0A0A0A] border border-white/10 rounded-lg py-2.5 text-sm font-semibold hover:bg-white/5 transition"
              >
                Cancel
              </button>
              <button
                onClick={markAllAvailable}
                disabled={markingAll}
                className="bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white rounded-lg py-2.5 text-sm font-semibold transition"
              >
                {markingAll ? "Updating…" : "Confirm"}
              </button>
            </div>
          </div>
        </div>
      )}
    </AppLayout>
  );
}
